import React, { useEffect, useState } from "react";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import FloatCard from "./FloatCard";
import { useTodaysGuidance } from "./useTodaysGuidance";
import {
  CADENCE_BADGE_STYLES,
  CADENCE_GENERIC_LABEL,
  CADENCE_ICON,
  CADENCE_ORDER,
  CadenceKey,
} from "../../Utils/Guidance/guidanceCadence";
import {
  registerGuidanceDrawer,
  GuidanceDrawerOpenOptions,
} from "../../Utils/Guidance/guidanceDrawerBus";
import { getCurrentGuidanceLinkKey } from "../../Utils/Guidance/guidanceLinkResolver";
import { GuidanceRole } from "../../Utils/Guidance/guidanceRole";
import SidebarModal from "../SidebarModal";

import "./style.scss";

const PULSE_SEEN_PREFIX = "eb_guidance_pulse_seen_";

type DrawerState = "closed" | "open" | "mini";

interface GuideDrawerProps {
  userRole: string | { name?: string } | null | undefined;
}

/**
 * Persistent Guide icon + slide-out drawer + Float mini-card (design-note.md
 * §5, §6.3, confirmed against the live interactive prototype), present on
 * every non-admin page. Content is page-aware: when the current route
 * matches one of the open cadence's own link targets, the drawer shows a
 * "you're on this page" banner and reorders content around it instead of
 * always showing the generic, page-agnostic view.
 */
const GuideDrawer: React.FC<GuideDrawerProps> = ({ userRole }) => {
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerState, setDrawerState] = useState<DrawerState>("closed");
  const [floatOpen, setFloatOpen] = useState(false);
  const [floatCadence, setFloatCadence] = useState<CadenceKey | null>(null);
  const [overrideCadence, setOverrideCadence] = useState<CadenceKey | null>(
    null
  );
  const [shouldPulse, setShouldPulse] = useState(false);

  const {
    role,
    setPersonaOverride,
    cadenceKey,
    isToday,
    cadenceLabel,
    playbook,
    personaContent,
    resolvedLinks,
    navLinks,
  } = useTodaysGuidance(userRole, { cadenceOverride: overrideCadence });

  useEffect(() => {
    try {
      const pulseKey = `${PULSE_SEEN_PREFIX}${location.pathname}`;
      if (!localStorage.getItem(pulseKey)) {
        setShouldPulse(true);
        localStorage.setItem(pulseKey, "1");
      } else {
        setShouldPulse(false);
      }
    } catch {
      setShouldPulse(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const applyOpts = (opts?: GuidanceDrawerOpenOptions) => {
      if (opts?.persona) setPersonaOverride(opts.persona);
      setOverrideCadence(opts?.cadenceKey || null);
    };
    return registerGuidanceDrawer(
      opts => {
        applyOpts(opts);
        setFloatOpen(false);
        setDrawerState("open");
      },
      opts => {
        applyOpts(opts);
        setDrawerState("closed");
        setFloatCadence(opts?.cadenceKey || null);
        setFloatOpen(true);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const withOrg = (path: string) => (orgSlug ? `/${orgSlug}${path}` : path);

  // A Hub-page card's "Float" (and the daily modal / home widget's "Let's
  // Go") open the drawer/float with an *explicit* role/cadence — e.g.
  // whichever role tab happens to be selected on the Hub page, not
  // necessarily the viewer's own role default. That override has to be
  // cleared on an actual close, or it silently outlives the interaction
  // that set it: the next time the viewer opens the drawer from the plain
  // compass icon (no options at all), it would otherwise keep showing that
  // stale role instead of falling back to their real default. Not called
  // on the Drawer<->Float "Full View"/"Float" transitions, which are
  // meant to carry the same context forward, not reset it.
  const resetOverrides = () => {
    setPersonaOverride(null);
    setOverrideCadence(null);
  };

  const closeDrawer = () => {
    setDrawerState("closed");
    resetOverrides();
  };

  const closeFloat = () => {
    setFloatOpen(false);
    resetOverrides();
  };

  const goTo = (path: string) => {
    setDrawerState("mini");
    navigate(withOrg(path));
  };

  const openFloatFromDrawer = () => {
    setFloatCadence(cadenceKey);
    setFloatOpen(true);
    setDrawerState("closed");
  };

  const expandFloatToDrawer = (ck: CadenceKey) => {
    setOverrideCadence(ck);
    setFloatOpen(false);
    setDrawerState("open");
  };

  const style = CADENCE_BADGE_STYLES[cadenceKey];
  const personaLabel = (personaContent.personaLabel ?? "").toUpperCase();

  // Page-awareness: does the current route match one of THIS playbook's own
  // link targets? If so, show the contextual banner + reordered content.
  const matchedLinkKey = getCurrentGuidanceLinkKey(
    location.pathname,
    location.search,
    navLinks
  );
  const matchedLink = matchedLinkKey
    ? resolvedLinks.find(l => l.key === matchedLinkKey)
    : undefined;

  return (
    <>
      {drawerState !== "open" && !floatOpen && (
        <button
          type="button"
          aria-label="Open Today's Playbook guide"
          title="Today's Playbook"
          onClick={() =>
            setDrawerState(prev => {
              if (prev === "open") {
                resetOverrides();
                return "closed";
              }
              return "open";
            })
          }
          className={`guidance-guide-icon w-9 h-9 rounded-[9px] shadow-sm flex items-center justify-center text-[17px] leading-none transition-colors ${
            shouldPulse ? "pulse-once" : ""
          }`}
          style={{
            backgroundColor: "#eff4ff",
            borderWidth: 1,
            borderColor: "#bfcefe",
          }}
        >
          <span role="img" aria-hidden="true">
            🧭
          </span>
        </button>
      )}

      {drawerState === "mini" && (
        <button
          type="button"
          onClick={() => setDrawerState("open")}
          className="guidance-mini-strip text-xs font-semibold px-1.5 py-3 rounded-l-md border border-r-0"
          style={{
            backgroundColor: style.bg,
            color: style.text,
            borderColor: style.border,
          }}
        >
          {cadenceLabel}
        </button>
      )}

      {drawerState === "open" && (
        <SidebarModal
          isOpen
          onClose={closeDrawer}
          className="guidance-guide-drawer"
          title={
            <div className="flex items-center gap-2">
              <span
                role="img"
                aria-hidden="true"
                className="text-lg leading-none shrink-0"
              >
                🧭
              </span>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wide text-[#2563eb]">
                  Playbooks Hub
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-bold text-[#0f1929]">
                    {cadenceLabel} Playbook
                  </span>
                  {isToday && (
                    <span className="text-[10px] font-semibold text-white bg-purple-600 rounded-full px-2 py-0.5">
                      Today
                    </span>
                  )}
                </div>
                <div className="text-[11px] font-normal text-[#6b7a8d] mt-px">
                  {personaLabel} · {playbook.time}
                </div>
              </div>
            </div>
          }
          headerActions={
            <button
              type="button"
              onClick={openFloatFromDrawer}
              className="text-xs font-semibold text-purple-600 hover:text-purple-700 border border-purple-200 rounded-md px-2 py-1 mr-1"
            >
              Float ↗
            </button>
          }
          footer={
            <>
              <div className="flex items-center justify-between px-4 py-3">
                <button
                  type="button"
                  onClick={() =>
                    goTo(
                      `/guidance/playbooks?role=${role}&cadence=${cadenceKey}`
                    )
                  }
                  className="text-xs font-semibold text-[#2563eb] hover:underline"
                >
                  View Full Playbook →
                </button>
                <button
                  type="button"
                  onClick={() =>
                    goTo(`/guidance/playbooks?role=${role}&openRhythm=1`)
                  }
                  className="text-xs font-semibold text-[#2563eb] hover:underline"
                >
                  Rhythm &amp; Outcomes →
                </button>
              </div>
              <div className="text-right text-[11px] text-gray-400 italic px-4 pb-2.5">
                AL = Account Level · RL = Relationship Level
              </div>
            </>
          }
        >
          {matchedLink && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-[9px] mb-3">
              <div className="text-[11px] font-bold text-[#15803d] mb-[3px]">
                ✓ You&apos;re on {matchedLink.label}
              </div>
              <div className="text-[11px] text-[#166534]">
                Focus on what to look for below — you&apos;re in the right
                place.
              </div>
            </div>
          )}

          <div className="mb-4">
            <div
              className="text-[11px] font-bold uppercase tracking-wide mb-1.5 flex items-center gap-1.5"
              style={{ color: style.text }}
            >
              <span>{CADENCE_ICON[cadenceKey]}</span>
              <span>Cadence: {cadenceLabel}</span>
            </div>
            <h3 className="font-bold text-sm text-gray-900 break-words">
              {playbook.heading}
            </h3>
            {!matchedLink && playbook.nav?.trim() && (
              <p className="text-xs text-gray-400 italic mt-1 break-words">
                ↗ {playbook.nav.trim()}
              </p>
            )}
          </div>

          {playbook.agenda?.length ? (
            <div className="mb-4 min-w-0">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Agenda
              </h4>
              <ol className="space-y-1.5">
                {playbook.agenda.map(item => (
                  <li
                    key={item.n}
                    className="text-sm text-gray-700 flex gap-2 min-w-0"
                  >
                    <span className="text-gray-400 shrink-0">{item.n}.</span>
                    <span className="min-w-0 flex-1 break-words">
                      {item.item}
                    </span>
                    <span className="text-xs text-gray-400 shrink-0">
                      {item.time}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}

          {!matchedLink && (
            <div className="mb-4 min-w-0">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                What to do
              </h4>
              <ul className="space-y-1.5">
                {playbook.what.map((line, idx) => (
                  <li
                    key={idx}
                    className="text-sm text-gray-700 flex gap-1.5 min-w-0"
                  >
                    <span className="text-[#2563eb] font-bold shrink-0">→</span>
                    <span className="min-w-0 break-words">{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {playbook.lookfor?.length ? (
            <div className="mb-4 min-w-0">
              <h4
                className={`text-xs font-semibold uppercase tracking-wide mb-2 ${
                  matchedLink ? "text-green-700" : "text-gray-500"
                }`}
              >
                What to look for
              </h4>
              <ul className="space-y-1.5">
                {playbook.lookfor.map((line, idx) => (
                  <li
                    key={idx}
                    className="text-sm text-gray-700 flex gap-1.5 min-w-0"
                  >
                    <span className="text-[#2563eb] font-bold shrink-0">→</span>
                    <span className="min-w-0 break-words">{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {matchedLink && (
            <div className="mb-4 min-w-0">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                What to do next
              </h4>
              <ul className="space-y-1.5">
                {playbook.what.map((line, idx) => (
                  <li
                    key={idx}
                    className="text-sm text-gray-700 flex gap-1.5 min-w-0"
                  >
                    <span className="text-[#2563eb] font-bold shrink-0">→</span>
                    <span className="min-w-0 break-words">{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {resolvedLinks.length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {matchedLink ? "Related pages" : "Jump to"}
              </h4>
              <div className="flex flex-wrap gap-2">
                {resolvedLinks.map(link => {
                  const isHere = link.key === matchedLinkKey;
                  return (
                    <button
                      key={link.key}
                      type="button"
                      disabled={isHere}
                      onClick={() => !isHere && goTo(link.path)}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-[7px] border transition-colors ${
                        isHere
                          ? "border-[#86efac] bg-[#f0fdf4] text-[#15803d] cursor-default"
                          : "border-[#bfcefe] bg-[#eff4ff] text-[#2563eb] hover:bg-[#dbeafe]"
                      }`}
                    >
                      {isHere ? "✓" : "↗"} {link.label}
                      {isHere ? " · You're here" : ""}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mb-1 pt-3 border-t border-gray-100">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Switch cadence
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {CADENCE_ORDER.filter(k => k !== cadenceKey).map(k => {
                const pillStyle = CADENCE_BADGE_STYLES[k];
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setOverrideCadence(k)}
                    className="text-xs font-medium px-2.5 py-1 rounded-full border hover:brightness-95"
                    style={{
                      backgroundColor: pillStyle.bg,
                      color: pillStyle.text,
                      borderColor: pillStyle.border,
                    }}
                  >
                    {CADENCE_ICON[k]} {CADENCE_GENERIC_LABEL[k]}
                  </button>
                );
              })}
            </div>
          </div>
        </SidebarModal>
      )}

      {floatOpen && floatCadence && (
        <FloatCard
          userRole={userRole}
          role={role as GuidanceRole}
          cadenceKey={floatCadence}
          onClose={closeFloat}
          onFullView={expandFloatToDrawer}
        />
      )}
    </>
  );
};

export default GuideDrawer;
